import { apiErrorField, apiErrorMessage } from '@agos/api-client'

import { tt } from './i18n'
import { formatCents, lineTotalCents, parseMoneyToCents, type MoneyInput } from './money'

/**
 * The virtual receipt — what gets printed when the register is in virtual mode.
 *
 * §11.4 / §14.2: a virtual receipt has no UID because it never reached the tax
 * authority's device. It is **not a fiscal document**, and the footer has to say
 * so, because a customer handed one has no way to tell otherwise.
 *
 * Ported from legacy `pages/restaurant.tsx`'s `printVirtualReceipt`, which
 * `dispatch.tsx` and `fiskal-receipts.tsx` also want. It lives in
 * `packages/shared` for that reason: three modules need it and §5 forbids one
 * importing another, so a module was the wrong home — the boundary lint said so
 * before the first commit.
 *
 * Two things it does differently, both on the tax numbers. See FB-063 and FB-064.
 */

export interface ReceiptLine {
  name?: string
  productName?: string
  quantity?: number
  unitPrice?: MoneyInput
  totalPrice?: MoneyInput
}

export interface VirtualReceipt {
  orderNumber?: string
  issuedAt?: string
  paymentMethod?: string
  total?: MoneyInput
  /** The server's numbers. Preferred over anything computed here. */
  vatRate?: MoneyInput
  vatBase?: MoneyInput
  vatAmount?: MoneyInput
  restaurantName?: string
  restaurantAddress?: string
  restaurantIco?: string
  restaurantDic?: string
  items?: ReceiptLine[]
}

const PAYMENT_LABELS: Record<string, string> = {
  cash: tt('Hotovosť'),
  card_terminal: tt('Karta – terminál'),
  card_courier: tt('Karta – kuriér'),
  card_online: tt('CardPay Online'),
}

export function paymentLabel(method: string | undefined): string {
  return PAYMENT_LABELS[method ?? ''] ?? tt('Karta')
}

/**
 * HTML escaping, because the receipt is assembled as a string and product names
 * come from the menu editor.
 *
 * FB-063: legacy interpolates `${i.name}` raw. A product called *Coca-Cola 0,5l &
 * hranolky* prints a broken entity, and one containing a `<` swallows the rest of
 * the row. These are staff-entered rather than customer-entered, so it is a
 * correctness problem on a printed document rather than a security hole — but the
 * document is what a customer is handed.
 */
export function escapeHtml(value: string | number | null | undefined): string {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

export interface ReceiptTax {
  ratePercent: number
  baseCents: number
  vatCents: number
  /** True when the frontend had to work it out itself. */
  derived: boolean
}

/** Slovakia's rates are 5, 19 and 23. 20 has not existed since 2011. */
const NO_RATE = 0

/**
 * The tax block.
 *
 * FB-064: legacy computes this client-side from the gross total with
 * `vatRate ?? 20` — a rate Slovakia retired in 2011, and one the §10A DPH upgrade
 * replaced with per-category 5 / 19 / 23. The endpoint already sends `vatRate`,
 * `vatBase` and `vatAmount` computed server-side and reconciling to the cent
 * (10,15 € = 8,53 + 1,62 at 19% in the dumps), so those are used.
 *
 * The reverse-charge fallback stays for a payload that predates those fields, but
 * it never invents a rate: with no rate there is no VAT line rather than a wrong
 * one.
 */
export function receiptTax(receipt: VirtualReceipt): ReceiptTax {
  const totalCents = parseMoneyToCents(receipt.total)
  const ratePercent = Number(receipt.vatRate ?? NO_RATE)

  const serverBase = receipt.vatBase === undefined ? null : parseMoneyToCents(receipt.vatBase)
  const serverVat = receipt.vatAmount === undefined ? null : parseMoneyToCents(receipt.vatAmount)
  if (serverBase !== null && serverVat !== null) {
    return { ratePercent, baseCents: serverBase, vatCents: serverVat, derived: false }
  }

  if (!Number.isFinite(ratePercent) || ratePercent <= 0) {
    return { ratePercent: 0, baseCents: totalCents, vatCents: 0, derived: true }
  }

  // Gross → net. The total is what was charged, so the base is derived from it
  // rather than the other way round.
  const base = Math.round(totalCents / (1 + ratePercent / 100))
  return { ratePercent, baseCents: base, vatCents: totalCents - base, derived: true }
}

export function lineName(line: ReceiptLine): string {
  return line.name ?? line.productName ?? tt('Položka')
}

export function lineTotal(line: ReceiptLine): number {
  if (line.totalPrice !== undefined) return parseMoneyToCents(line.totalPrice)
  return lineTotalCents(line.unitPrice, line.quantity ?? 0)
}

const DATE_FORMAT = new Intl.DateTimeFormat('sk-SK', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

/**
 * The receipt as printable HTML.
 *
 * A string rather than a component because it is printed in its own window with
 * its own stylesheet, at 80 mm — the app's styles have nothing to say about a
 * thermal printer.
 */
export function renderVirtualReceipt(receipt: VirtualReceipt, now: number): string {
  const tax = receiptTax(receipt)
  const totalCents = parseMoneyToCents(receipt.total)
  const issued = DATE_FORMAT.format(new Date(receipt.issuedAt ?? now))

  const rows = (receipt.items ?? [])
    .map(
      (line) =>
        `<tr><td style="width:50%">${escapeHtml(lineName(line))}</td>` +
        `<td style="text-align:center">${escapeHtml(line.quantity ?? 0)}×</td>` +
        `<td style="text-align:right">${escapeHtml(formatCents(lineTotal(line)))}</td></tr>`,
    )
    .join('')

  const vatRows =
    tax.ratePercent > 0
      ? `<tr class="small"><td>${escapeHtml(tt('Základ DPH'))} (${tax.ratePercent}%)</td>` +
        `<td style="text-align:right">${escapeHtml(formatCents(tax.baseCents))}</td></tr>` +
        `<tr class="small"><td>DPH (${tax.ratePercent}%)</td>` +
        `<td style="text-align:right">${escapeHtml(formatCents(tax.vatCents))}</td></tr>`
      : ''

  return `<!DOCTYPE html><html lang="sk"><head><meta charset="utf-8"><title>${escapeHtml(tt('Doklad'))}</title>
<style>
  body{font-family:monospace;font-size:12px;padding:12px;max-width:340px;margin:0 auto}
  h2{font-size:15px;text-align:center;margin:0 0 2px}
  .center{text-align:center} .small{font-size:11px;color:#555}
  table{width:100%;border-collapse:collapse}td{padding:2px 3px}
  hr{border:none;border-top:1px dashed #999;margin:8px 0}
  .total-row{font-size:14px;font-weight:bold}
  .footer{font-size:10px;color:#777;text-align:center;margin-top:10px}
  @media print{@page{margin:4mm}}
</style></head><body>
<h2>${escapeHtml(receipt.restaurantName ?? 'PizzaMajstro')}</h2>
${receipt.restaurantAddress ? `<p class="center small">${escapeHtml(receipt.restaurantAddress)}</p>` : ''}
${
  receipt.restaurantIco
    ? `<p class="center small">${escapeHtml(tt('IČO:'))} ${escapeHtml(receipt.restaurantIco)}${
        receipt.restaurantDic ? ` | ${escapeHtml(tt('DIČ:'))} ${escapeHtml(receipt.restaurantDic)}` : ''
      }</p>`
    : ''
}
<hr>
<p class="small center">${escapeHtml(tt('Doklad č.:'))} ${escapeHtml(receipt.orderNumber ?? '')} &nbsp;|&nbsp; ${escapeHtml(issued)}</p>
<hr>
<table><thead><tr><th style="text-align:left">${escapeHtml(tt('Položka'))}</th><th>${escapeHtml(tt('Mn.'))}</th><th style="text-align:right">${escapeHtml(tt('Cena'))}</th></tr></thead>
<tbody>${rows}</tbody></table>
<hr>
<table>
  ${vatRows}
  <tr class="total-row"><td>${escapeHtml(tt('CELKOM'))}</td><td style="text-align:right">${escapeHtml(formatCents(totalCents))}</td></tr>
</table>
<hr>
<p class="small center">${escapeHtml(tt('Platba:'))} ${escapeHtml(paymentLabel(receipt.paymentMethod))}</p>
<p class="footer">${escapeHtml(tt('Virtuálna kasa - doklad nie je daňovým dokladom ERP'))}</p>
</body></html>`
}

/**
 * Opens the receipt in its own window and prints it.
 *
 * The print script is attached as a handler rather than written into the markup:
 * a `<script>` tag inside a `document.write` string is what makes the whole thing
 * unescapable, and the caller wants the print either way.
 */
export function printVirtualReceipt(receipt: VirtualReceipt, now: number): boolean {
  const target = window.open('', '_blank', 'width=380,height=700')
  if (!target) return false

  target.document.write(renderVirtualReceipt(receipt, now))
  target.document.close()
  target.onload = () => {
    target.print()
    setTimeout(() => target.close(), 600)
  }
  return true
}

/**
 * Whether a refused print means **the receipt already exists** - §9.362, FB-649.
 *
 * `FiskalReceiptService` answers `409` with code `ALREADY_PRINTED` and a sentence that carries the
 * document's number: *"Fiskálny doklad pre túto objednávku bol už vytlačený (č. X). Ak potrebujete
 * nový doklad, najprv stornujte pôvodný."* **That is a statement about state, not a failure to
 * print** - and two screens were reporting it as one: the till as a destructive *"Pokladňa - chyba"*,
 * and the dispatch board by counting the order among the failures, which sends somebody to chase a
 * receipt that is already on the tray.
 *
 * **Here rather than in either module** for the reason this file's index entry already gives: the
 * restaurant till, the dispatch board and the fiscal-receipts page all print, and §5 forbids a module
 * owning what all three need. The three callers of `POST /fiskal/receipt` are those same three.
 *
 * **Read through `apiErrorField`**, which is the client's own envelope reader. Spelling
 * `error.data.code` here by hand is what FB-645 was: nine handlers reading a property `ApiError`
 * does not have, each of them plausible on its own.
 *
 * **Why an operator meets this at all**, beyond a genuine duplicate: a first attempt whose answer
 * was lost. The owner's 2026-09-18 incident is the shape - the kasa prints, the connection is cut
 * before the answer arrives, the retry is refused as a duplicate, and the only thing on screen is
 * whatever this decides to say.
 */
export function alreadyPrinted(error: unknown): boolean {
  return apiErrorField(error, 'code') === ALREADY_PRINTED
}

/** The backend's own code, named once. */
const ALREADY_PRINTED = 'ALREADY_PRINTED'

/**
 * What to tell the operator when a print was refused because the document exists.
 *
 * The server's sentence when there is one - it names the receipt number, which nothing on this side
 * knows - and a fallback that says the same thing without inventing one.
 */
export function alreadyPrintedNote(error: unknown): string {
  return apiErrorMessage(error, tt('Fiskálny doklad pre túto objednávku už bol vytlačený.'))
}

